import React, { useState } from 'react';
import { FORM_MODE, Permissions, REST_API } from '@awp/constants';
import { Card, Container, Row } from 'react-bootstrap';
import { Form, Formik } from 'formik';
import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import AwpFormActionButtons from '@awp/components/Common/AwpFormActionButtons';
import AwpSpinner from '@awp/components/Common/AwpSpinner';
import { AwpContainer } from '@components/Common/AwpContainer';
import Authorize from '@components/Authorize';
import { HazardForecastTemplateService } from '@components/HazardForecastTemplate/HazardForecastTemplateService';
import { useSelector } from 'react-redux';
import { getAvalancheGeographicAreas } from '@stores/avalancheSlice';
import { selectAllHazardForecastDropDown } from '@stores/hazardForecastSlice';
import { TreeSelectHelper } from '@components/Common/Helpers/TreeSelectHelper';
import { GetJson, PutJson } from '@awp/helpers/fetchReact';
import { selectActiveWeatherStations } from '@stores/weatherStationSlice';
import { toast } from 'react-toastify';

const HazardForecastTemplateList = () => {
  const [entityErrors, setEntityErrors] = useState(null);
  const [loading, setLoading] = useState(false);

  const selectAvalanchePrograms = useSelector((state) =>
    getAvalancheGeographicAreas(state, 'AVALANCHE PROGRAM'),
  ).filter((program) => !program.isDisabled);
  const [avalanchePrograms, setAvalanchePrograms] = useState(selectAvalanchePrograms);

  const allUnassociatedWeatherStations = useSelector(selectActiveWeatherStations);
  const [unassociatedWeatherStations, setUnassociatedWeatherStations] = useState(allUnassociatedWeatherStations);
  const [associatedWeatherStations, setAssociatedWeatherStations] = useState([]);
  const allHazardForecastDropDowns = useSelector((state) =>
    TreeSelectHelper.filterOutDisabled(selectAllHazardForecastDropDown(state)),
  );
  const [avalancheAreas, setAvalancheAreas] = useState(allHazardForecastDropDowns);
  const [program, setProgram] = useState(undefined);
  const [selectedAvalancheArea, setSelectedAvalancheArea] = useState(undefined);
  const [selectedWeatherStations, setSelectedWeatherStations] = useState(undefined);

  const [weatherStationsToAdd, setWeatherStationsToAdd] = useState([]);
  const [weatherStationsToRemove, setWeatherStationsToRemove] = useState([]);

  const formikRef = React.useRef(null);
  const crudService = new HazardForecastTemplateService(FORM_MODE.LIST);
  const { entityName } = crudService.crud;

  const onWeatherStationChange = (weatherStations) => {
    setSelectedWeatherStations(weatherStations ?? undefined);
  };

  const onProgramChange = (program) => {
    setProgram(program);

    if (program?.value) {
      setAvalancheAreas(TreeSelectHelper.filterByParentId(allHazardForecastDropDowns, program?.value));
    } else {
      setAvalancheAreas(allHazardForecastDropDowns);
    }

    setSelectedAvalancheArea(undefined);
    resetWeatherStation();
  };

  const onAreaChange = (arr, selectedNodes) => {
    setLoading(true);

    setSelectedAvalancheArea(selectedNodes);
    setAvalancheAreas(TreeSelectHelper.updateCheckedFieldsFromSelectedNodes(arr, selectedNodes));

    if (selectedNodes.length > 0) {
      GetJson(`${REST_API.WEATHER_STATIONS_AREAS}${selectedNodes[0].value}`)
        .then((response) => response.json())
        .then((data) => {
          setAssociatedWeatherStations(data);

          const filteredWeatherStations = allUnassociatedWeatherStations.filter(
            ({ DropdownText: dt1 }) => !data.some(({ DropdownText: dt2 }) => dt1 === dt2),
          );
          setUnassociatedWeatherStations(filteredWeatherStations);
        });
    }

    resetWeatherStation();

    setTimeout(() => {
      setLoading(false);

      formikRef.current.validateField('area');
    });
  };

  const resetWeatherStation = () => {
    setLoading(true);

    setAssociatedWeatherStations([]);
    setUnassociatedWeatherStations(allUnassociatedWeatherStations);
    setSelectedWeatherStations([]);
    resetWeatherStationsToModify();

    setTimeout(() => {
      setLoading(false);
    });
  };

  const resetWeatherStationsToModify = () => {
    setWeatherStationsToAdd([]);
    setWeatherStationsToRemove([]);
  };

  const handleSubmit = (values, { setSubmitting, setErrors }) => {
    setSubmitting(true);

    const url = '/hazardforecastTemplate';
    PutJson(url, {
      GeoGraphicArea: selectedAvalancheArea[0],
      weatherStationsToAdd,
      weatherStationsToRemove,
    })
      .then(async (response) => {
        if (response.ok) {
          toast.success('Hazard Forecast Template updated successfully.');
          resetWeatherStationsToModify();
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          if (errors.entity) setEntityErrors({ Entity: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const onClickCancel = () => {
    setLoading(true);

    setAvalanchePrograms(selectAvalanchePrograms);
    setProgram(undefined);
    setAvalancheAreas(allHazardForecastDropDowns);
    setSelectedAvalancheArea(undefined);

    resetWeatherStation();

    setTimeout(() => {
      setLoading(false);
    });
  };

  const onClickDelete = (weatherStationId) => {
    setLoading(true);

    const foundAssociatedWeatherStation = associatedWeatherStations.find((ws) => ws.Id === weatherStationId);

    // Remove from list of Weather Stations to associate
    if (weatherStationsToAdd.includes(foundAssociatedWeatherStation)) {
      setWeatherStationsToAdd(weatherStationsToAdd.filter((ws) => ws.Id !== weatherStationId));
    } else {
      setWeatherStationsToRemove([...weatherStationsToRemove, foundAssociatedWeatherStation || []]);
    }

    // Add to Unassociated Weather Stations Dropdown
    const newUnassociatedWeatherStations = [...unassociatedWeatherStations, foundAssociatedWeatherStation].sort(
      (a, b) => a.Number.localeCompare(b.Number),
    );
    setUnassociatedWeatherStations(newUnassociatedWeatherStations);

    // Remove from table
    setAssociatedWeatherStations(associatedWeatherStations.filter((ws) => ws.Id !== weatherStationId));

    setTimeout(() => {
      setLoading(false);
    });
  };

  const onClickAdd = () => {
    setLoading(true);

    let newWeatherStationsToRemove = [...weatherStationsToRemove];
    let newWeatherStationsToAdd = [...weatherStationsToAdd];
    selectedWeatherStations.forEach((ws) => {
      // Remove from list of Weather Stations to un-associate
      if (weatherStationsToRemove.includes(ws)) {
        newWeatherStationsToRemove = newWeatherStationsToRemove.filter((wsr) => ws.Id !== wsr.Id);
      } else {
        newWeatherStationsToAdd.push(ws);
      }
    });

    setWeatherStationsToAdd(newWeatherStationsToAdd);
    setWeatherStationsToRemove(newWeatherStationsToRemove);

    setUnassociatedWeatherStations(unassociatedWeatherStations.filter((ws) => !selectedWeatherStations.includes(ws)));
    setAssociatedWeatherStations([...associatedWeatherStations, ...selectedWeatherStations]);
    setSelectedWeatherStations(undefined);

    setTimeout(() => {
      setLoading(false);
    });
  };

  return (
    <>
      <AwpContainer>
        <Authorize requires={[Permissions.ReadAdminTools]}>
          {loading ? (
            <Container>
              <AwpSpinner />
            </Container>
          ) : (
            <>
              <Formik
                innerRef={formikRef}
                validateOnMount={false}
                initialValues={{
                  ...HazardForecastTemplateService.mapInitialValues({}),
                  avalancheProgram: program,
                  area: selectedAvalancheArea,
                  unassociatedWeatherStations: selectedWeatherStations,
                }}
                validationSchema={HazardForecastTemplateService.getValidations({
                  selectedAvalancheArea,
                })}
                onSubmit={handleSubmit}
              >
                {(props) => {
                  const myProps = {
                    ...props,
                    dirty: weatherStationsToAdd?.length > 0 || weatherStationsToRemove?.length > 0,
                  };

                  return (
                    <Form>
                      <Card border="light" className="page-card">
                        <Card.Header>
                          <Row className="mt-4"></Row>
                          {crudService.crud.getHeader()}
                        </Card.Header>
                        <Card.Body>
                          {HazardForecastTemplateService.getFields({
                            avalanchePrograms,
                            avalancheAreas,
                            associatedWeatherStations,
                            unassociatedWeatherStations,
                            selectedAvalancheArea,
                            selectedWeatherStations,
                            onProgramChange,
                            onAreaChange,
                            onWeatherStationChange,
                            onClickDelete,
                            onClickAdd,
                          })}
                        </Card.Body>
                        <Card.Footer>
                          <Row className="my-2">
                            <AwpFormActionButtons
                              title=" "
                              id={2}
                              mode={FORM_MODE.CREATE}
                              buttonLabel={'Save'}
                              entityName={entityName}
                              onClickCancel={onClickCancel}
                              permissions={[Permissions.WriteAdminTools]}
                              readOnly={false}
                              isAlignLeft={true}
                              btnCancelName={'Close'}
                              props={{ ...myProps }}
                              isPristine={!myProps.dirty || props.isSubmitting}
                            />
                          </Row>
                        </Card.Footer>
                      </Card>
                    </Form>
                  );
                }}
              </Formik>
              <AwpValidationErrorModal setErrors={setEntityErrors} errors={entityErrors} />
            </>
          )}
        </Authorize>
      </AwpContainer>
    </>
  );
};

export default HazardForecastTemplateList;
